/**
 * OaSIS Explorer Frontend - Theme Context
 * Task 3.1: Frontend Foundation & Global Features
 * 
 * Global theme state management with localStorage persistence
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { Theme, ThemeContextType } from '../types';
import { themeStorage, systemTheme } from '../utils/localStorage';

// Create the context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Context provider props
interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Theme Context Provider
 * Manages global theme state with localStorage persistence and system preference detection
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const [theme, setThemeState] = useState<Theme>(() => {
    return themeStorage.get();
  });

  /**
   * Apply theme to document root
   */
  const applyTheme = (newTheme: Theme) => {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('light', 'dark');
    
    // Add new theme class
    root.classList.add(newTheme);
    
    // Set data attribute for CSS custom properties
    root.setAttribute('data-theme', newTheme);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content', 
        newTheme === 'dark' ? '#0f172a' : '#ffffff' // Using Tailwind gray-900 for dark, white for light
      );
    }
  };

  /**
   * Set theme with localStorage persistence
   */
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    themeStorage.set(newTheme);
    applyTheme(newTheme);
    
    // Dispatch custom event for other components that might need to know
    window.dispatchEvent(new CustomEvent('themeChanged', { 
      detail: { theme: newTheme } 
    }));
  };

  /**
   * Toggle between light and dark themes
   */
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  // Apply initial theme
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const cleanup = systemTheme.onChange((systemThemeValue) => {
      // Only auto-switch if user hasn't set a preference
      const storedTheme = themeStorage.get();
      if (storedTheme === systemThemeValue) {
        // User's stored preference matches system, so they likely want auto-switching
        setThemeState(systemThemeValue);
        applyTheme(systemThemeValue);
      }
    });

    return cleanup;
  }, []);

  const contextValue: ThemeContextType = {
    theme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to use theme context
 * Throws error if used outside of ThemeProvider
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

/**
 * Hook to get current theme value only
 * Convenience hook for components that only need to read the theme
 */
export const useCurrentTheme = (): Theme => {
  const { theme } = useTheme();
  return theme;
};

/**
 * Higher-order component to provide theme context
 */
export const withTheme = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => (
    <ThemeProvider>
      <Component {...props} />
    </ThemeProvider>
  );
};
